'use client';

// Branding theo subdomain (white-label): {tenant}.gms.ledifa.com → áp logo + màu thương hiệu
// NGAY từ trang đăng nhập (trước khi có tài khoản). Resolve subdomain ở client từ hostname,
// gọi API công khai /api/tenant-branding, set CSS var --color-brand-* + cung cấp logo qua context.

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { logClientError } from './client-error-logger';

const BASE_DOMAIN = 'gms.ledifa.com';
const SHORT_BASE_DOMAIN = 'ledifa.com';
const ROOT_HOSTS = new Set(['gms', 'www', 'localhost']);

export interface SubdomainBranding {
  id: string;
  name: string;
  brandColor: string | null;
  logoUrl: string | null;
  logoMarkUrl: string | null;
}

const BrandingContext = createContext<SubdomainBranding | null>(null);

function subdomainOf(hostname: string): string | null {
  const h = hostname.split(':')[0];
  if (h.endsWith(`.${BASE_DOMAIN}`)) {
    const sub = h.slice(0, -(`.${BASE_DOMAIN}`.length));
    if (sub && !ROOT_HOSTS.has(sub) && /^[a-z0-9][a-z0-9-]*$/.test(sub)) return sub;
  }
  // Domain ngắn {tenant}.ledifa.com (trang P4S /sale) — cũng cần brand đúng tenant.
  if (h.endsWith(`.${SHORT_BASE_DOMAIN}`)) {
    const sub = h.slice(0, -(`.${SHORT_BASE_DOMAIN}`.length));
    if (sub && !ROOT_HOSTS.has(sub) && /^[a-z0-9][a-z0-9-]*$/.test(sub)) return sub;
  }
  return null;
}

// ── Màu: tạo dải shade 50..900 từ 1 màu brand (mix trắng = nhạt, mix đen = đậm) ──────
function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = Number.parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
const mix = (c: number, t: number, p: number) => Math.round(c + (t - c) * p);
function shade(rgb: [number, number, number], p: number): string {
  // p>0 → trộn trắng (nhạt); p<0 → trộn đen (đậm)
  const t = p >= 0 ? 255 : 0;
  const a = Math.abs(p);
  const [r, g, b] = rgb.map(c => mix(c, t, a)) as [number, number, number];
  return `#${[r, g, b].map(c => c.toString(16).padStart(2, '0')).join('')}`;
}

export function applyBrandColor(brandColor: string | null) {
  const root = document.documentElement;
  const SCALE: Record<number, string> = {};
  if (!brandColor) return;
  const rgb = hexToRgb(brandColor);
  if (!rgb) return;
  // 600 = màu gốc; nhạt dần lên 50, đậm dần xuống 900
  SCALE[600] = shade(rgb, 0);
  SCALE[50] = shade(rgb, 0.92); SCALE[100] = shade(rgb, 0.82); SCALE[200] = shade(rgb, 0.62);
  SCALE[300] = shade(rgb, 0.45); SCALE[400] = shade(rgb, 0.28); SCALE[500] = shade(rgb, 0.12);
  SCALE[700] = shade(rgb, -0.15); SCALE[800] = shade(rgb, -0.3); SCALE[900] = shade(rgb, -0.45);
  for (const [k, v] of Object.entries(SCALE)) root.style.setProperty(`--color-brand-${k}`, v);
}

export function BrandingProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const [branding, setBranding] = useState<SubdomainBranding | null>(null);

  useEffect(() => {
    const sub = subdomainOf(window.location.hostname);
    if (!sub) return; // domain chung → giữ brand mặc định Gardenia
    let cancelled = false;
    fetch(`/api/tenant-branding?subdomain=${encodeURIComponent(sub)}`, { signal: AbortSignal.timeout(15_000) })
      .then(r => r.json())
      .then((d: { tenant: SubdomainBranding | null }) => {
        if (cancelled || !d?.tenant) return;
        setBranding(d.tenant);
        applyBrandColor(d.tenant.brandColor);
      })
      .catch((err) => {
        logClientError({ route: 'lib/branding-context.tsx', action: 'load_subdomain_branding', message: err instanceof Error ? err.message : String(err), details: { subdomain: sub } });
        /* lỗi → giữ brand mặc định */
      });
    return () => { cancelled = true; };
  }, []);

  return <BrandingContext.Provider value={branding}>{children}</BrandingContext.Provider>;
}

/** Branding theo subdomain hiện tại (null nếu ở domain chung gms.ledifa.com). */
export function useBranding(): SubdomainBranding | null {
  return useContext(BrandingContext);
}

/** Logo dùng cho UI: ưu tiên logo tenant (subdomain), fallback ảnh mặc định. */
export function useBrandLogo(fallback: string): { src: string; alt: string } {
  const b = useBranding();
  return useMemo(() => ({ src: b?.logoUrl || fallback, alt: b?.name || 'GMS' }), [b, fallback]);
}
